import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';

const API_BASE = '/api';

interface Topic {
  id: string;
  name: string;
  partitions: number;
  replication_factor: number;
  retention_hours: number;
  status: string;
  events_per_second: number;
  total_events_today: number;
  avg_event_size_bytes: number;
  consumer_lag: number;
  created_date: string;
}

interface Statistics {
  total_topics: number;
  active_topics: number;
  total_subscriptions: number;
  active_subscriptions: number;
  events_per_second: number;
  events_today: number;
  total_consumer_lag: number;
  pending_messages: number;
  failed_events: number;
  avg_event_size_bytes: number;
}

export default function EventBusPage() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [topicsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/eventbus/topics`),
        fetch(`${API_BASE}/eventbus/statistics`)
      ]);
      setTopics(await topicsRes.json());
      setStatistics(await statsRes.json());
    } catch (error) {
      console.error('Error fetching event bus data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Event Bus</h1>
          <p className="mt-2 text-gray-600">Manage event topics, subscriptions, and message delivery</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Topics</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_topics}</p>
              <p className="text-sm text-green-600">{statistics.active_topics} active</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Events/Second</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.events_per_second)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Events Today</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.events_today)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Consumer Lag</p>
              <p className="text-2xl font-bold text-yellow-600">{formatNumber(statistics.total_consumer_lag)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Failed Events</p>
              <p className="text-2xl font-bold text-red-600">{statistics.failed_events}</p>
            </div>
          </div>
        )}

        {/* Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topics.map((topic) => (
            <div key={topic.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{topic.name}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  topic.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {topic.status}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Events/sec</span>
                  <span className="font-medium">{formatNumber(topic.events_per_second)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Events Today</span>
                  <span className="font-medium">{formatNumber(topic.total_events_today)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Partitions</span>
                  <span className="font-medium">{topic.partitions}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Consumer Lag</span>
                  <span className={`font-medium ${topic.consumer_lag > 1000 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {formatNumber(topic.consumer_lag)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Retention</span>
                  <span className="font-medium">{topic.retention_hours}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Avg Size</span>
                  <span className="font-medium">{(topic.avg_event_size_bytes / 1024).toFixed(1)} KB</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
